import { z } from 'zod';

export const VIDEO_BUCKET = 'tour-videos';
// Original imported media (including derived posters) rejected by the owner.
const EXCLUDED_VIDEO_PREFIXES = [
  '172d2bcb-574c-4242-8926-e6245b9b4002/import/',
  '172d2bcb-574c-4242-8926-e6245b9b4006/import/',
];
export function hasExcludedVideoMedia(video: { clip_path: string | null; full_path: string | null; poster_path: string | null }) {
  return [video.clip_path, video.full_path, video.poster_path].some(path =>
    path !== null && EXCLUDED_VIDEO_PREFIXES.some(prefix => path.startsWith(prefix)));
}
export const UNCONFIRMED_VIDEO_NAMES = ['por-confirmar-23-manglares-aves'];
export function isIgnoredVideoFilename(filename: string) {
  return filename.toLowerCase().includes('por-confirmar-24-snorkel');
}
export function confirmedVideoProductIds(filename: string, catalog?: { id: string; title: string; source: string }[]): string[] | null {
  const name = filename.toLowerCase().replace(/\.(mp4|webm)$/, '');
  if (name === 'sian-kaan') {
    if (!catalog) return ['880737']; // Verified public Bókun product.
    return [...new Set(catalog.filter(t => t.source === 'bokun' && /^\d+$/.test(t.id) &&
      /\bsian\s+ka\s*an\b/.test(t.title.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/['’\-]/g, ' ')))
      .map(t => t.id))];
  }
  return ['por-confirmar-02', 'tulum-coba-4x1-cenote-playa-del-carmen'].includes(name) ? ['1238530'] : null;
}
export const VIDEO_SUGGESTIONS = [
  ['Holbox', 'holbox, holbox-manglares, holbox-yalahau'],
  ['Cozumel', 'Solo completo cozumel-playa-san-martin; clip: primeros 9 segundos de ese completo (pendiente de recibir)'],
  ['Chichén Itzá / Valladolid', 'chichen-itza-valladolid, chichen-valladolid-suytun, chichen-valladolid-xkeken, chichen-itza-noche'],
  ['Tulum / cenotes', 'tulum-gran-cenote-dos-ojos, tulum-akumal-cenotes, tulum-cenote-multun-ha, tulum-playa-del-carmen-casa-tortuga, ruta-de-cenotes'],
  ['Tulum / Cobá · confirmado', 'tulum-coba-4x1-cenote-playa-del-carmen (antes por-confirmar-02) → producto 1238530'],
  ['Sian Ka’an · confirmado', 'sian-kaan → tours de Sian Ka’an del catálogo; encontrado: Oasis Privado (880737). Ignorar por-confirmar-24-snorkel'],
  ['Bacalar', 'bacalar, por-confirmar-05-laguna'], ['Las Coloradas', 'las-coloradas'], ['Isla Mujeres', 'isla-mujeres-snorkel'], ['Isla Contoy', 'Sin video por ahora; no usar clip isla-contoy'],
];
export const videoInput = z.object({
  id: z.string().uuid(), title: z.string().trim().min(1).max(160), destination: z.string().trim().min(1).max(100),
  clip_path: z.string().nullable(), full_path: z.string().nullable(), poster_path: z.string().nullable(),
  clip_duration: z.number().min(8.5).max(9.5).nullable(), full_duration: z.number().min(60).max(180).nullable(),
  status: z.enum(['draft', 'published']), sort_order: z.number().int().min(0).max(10000),
  productIds: z.array(z.string().regex(/^\d{1,12}$/)).max(30), confirmed: z.boolean().default(false),
}).superRefine((v, ctx) => {
  if (isIgnoredVideoFilename(v.title)) ctx.addIssue({ code: 'custom', message: 'Ignora el video antiguo; sube sian-kaan.mp4 con el logo corregido.' });
  if (hasExcludedVideoMedia(v)) ctx.addIssue({ code: 'custom', message: 'Estos archivos fueron excluidos por contener marca de agua de otra empresa.' });
  if (v.clip_path && !v.clip_duration) ctx.addIssue({ code: 'custom', message: 'Verifica la duración del clip.' });
  if (v.full_path && !v.full_duration) ctx.addIssue({ code: 'custom', message: 'Verifica la duración del video completo.' });
  if (v.status === 'published' && (!v.poster_path || (!v.clip_path && !v.full_path) || !v.productIds.length)) ctx.addIssue({ code: 'custom', message: 'Se requiere portada, video y al menos un tour confirmado.' });
  if (UNCONFIRMED_VIDEO_NAMES.some(n => v.title.toLowerCase().includes(n)) && v.productIds.length && !v.confirmed) ctx.addIssue({ code: 'custom', message: 'Este video necesita confirmación explícita antes de asignarlo.' });
});
export type VideoInput = z.infer<typeof videoInput>;
export type PublicTourVideo = { id: string; title: string; destination: string; clip: string | null; full: string | null; poster: string | null; productIds: string[]; sort_order: number };
export function cacheAgeMinutes(timestamp?: string, now = Date.now()) {
  if (!timestamp) return null;
  const time = Date.parse(timestamp);
  return Number.isFinite(time) && time <= now ? Math.floor((now-time)/60000) : null;
}
export function mayAutoplayVideo(saveData: boolean, reducedMotion: boolean, inView: boolean, visible: boolean) {
  return !saveData && !reducedMotion && inView && visible;
}